import { skipToken } from '@apollo/client'
import { differenceInMinutes, format } from 'date-fns'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { type ReactNode, use, useCallback, useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceArea,
  XAxis,
  YAxis,
} from 'recharts'
import { ChartTimeframeProviderContext } from '@/components/shared/analytics/chart-timeframe/context/context'
import { useChartZoom } from '@/components/shared/analytics/hooks/use-chart-zoom'
import { useLogsExplorer } from '@/components/shared/logs-explorer/hooks/use-logs-explorer'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/sv/chart'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { getXAxisDateFormat } from '@/config/charts'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useIdProject, useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  LogVolumeChartQuery,
  LogVolumeChartQueryVariables,
} from '@/components/shared/logs-explorer/components/log-volume-chart.types.gen'
import type { LogService, MetricsFilterInput } from '@/graphql.types'

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform)

const LOG_VOLUME_CHART_QUERY = gql`
  query LogVolumeChart(
    $idCompany: String!
    $idProject: String
    $idService: String!
    $serviceType: LogService!
    $timeframe: MetricsTimeFrameInput!
    $intervalInSeconds: Int
    $filters: [MetricsFilterInput!]
  ) {
    company(id: $idCompany) {
      id
      cloudflareMetrics(
        idService: $idService
        idProject: $idProject
        serviceType: $serviceType
        timeframe: $timeframe
        intervalInSeconds: $intervalInSeconds
        filters: $filters
      ) {
        idCompany
        timeframe {
          start
          end
        }
        logsVolume {
          trace
          debug
          time
          info
          warn
          error
          fatal
        }
      }
    }
  }
`

type Props = {
  idService: string
  serviceType: LogService
  filters?: MetricsFilterInput[]
}

const chartConfig = {
  trace: { label: 'Trace', color: 'oklch(0.554 0.046 257.417)' }, // slate-500
  debug: { label: 'Debug', color: 'oklch(0.606 0.25 292.717)' }, // violet-500
  info: { label: 'Info', color: 'oklch(0.685 0.169 237.323)' }, // sky-500
  warn: { label: 'Warn', color: 'oklch(0.769 0.188 70.08)' }, // amber-500
  error: { label: 'Error', color: 'var(--destructive)' },
  fatal: { label: 'Fatal', color: 'oklch(0.667 0.295 322.15)' }, // fuchsia-500
} satisfies ChartConfig

const SummaryBadge = ({
  color,
  label,
  value,
}: {
  color: string
  label: string
  value: number
}) => (
  <Badge variant="outline" className="p-0 px-2">
    <div className={cn(`h-2.5 w-2.5 rounded-xs`, color)} /> {label}
    <Separator orientation="vertical" className="h-5!" />{' '}
    <div className="font-bold">
      {value > 1000 ? `${Math.round(value / 100) / 10}k` : value}
    </div>
  </Badge>
)

export const LogVolumeChart = ({ idService, serviceType, filters }: Props) => {
  const { idCompany } = useIdCompany()
  const { idProject } = useIdProject()
  const { timeframe } = use(ChartTimeframeProviderContext)
  const { isChartCollapsed, setIsChartCollapsed } = useLogsExplorer()

  const minutesDiff = differenceInMinutes(
    timeframe.endDate ?? 0,
    timeframe.startDate ?? 0
  )

  const { data } = useSuspenseQuery<
    LogVolumeChartQuery,
    LogVolumeChartQueryVariables
  >(
    LOG_VOLUME_CHART_QUERY,
    !timeframe?.startDate || !timeframe?.endDate
      ? skipToken
      : {
          variables: {
            idCompany,
            idProject,
            idService,
            serviceType,
            intervalInSeconds: minutesDiff ?? 90,
            timeframe: {
              start: timeframe.startDate.toISOString(),
              end: timeframe.endDate.toISOString(),
            },
            filters: filters?.length ? filters : undefined,
          },
        }
  )

  const { chartData, summary } = useMemo(() => {
    const logsVolume = data?.company?.cloudflareMetrics?.logsVolume ?? []
    const totals = { trace: 0, debug: 0, info: 0, warn: 0, error: 0, fatal: 0 }

    const points = logsVolume.map((point) => {
      totals.trace += point.trace ?? 0
      totals.debug += point.debug ?? 0
      totals.info += point.info ?? 0
      totals.warn += point.warn ?? 0
      totals.error += point.error ?? 0
      totals.fatal += point.fatal ?? 0

      return {
        time: Number(point.time),
        trace: point.trace ?? 0,
        debug: point.debug ?? 0,
        info: point.info ?? 0,
        warn: point.warn ?? 0,
        error: point.error ?? 0,
        fatal: point.fatal ?? 0,
      }
    })

    return { chartData: points, summary: totals }
  }, [data?.company?.cloudflareMetrics?.logsVolume])

  const intervalMs = (minutesDiff ?? 90) * 1000

  const { chartProps, referenceAreaProps, isSelecting } = useChartZoom({
    intervalMs,
  })

  const tooltipLabelFormatter = useCallback(
    (
      _label: ReactNode,
      items: ReadonlyArray<{ payload?: { time?: number } }>
    ) => {
      const t = items?.[0]?.payload?.time
      return t ? format(t, 'MMM dd, yyyy HH:mm') : ''
    },
    []
  )

  const tooltipValueFormatter = useCallback((value: unknown) => {
    const numValue = Number(value)
    return numValue > 1000
      ? `${Math.round(numValue / 100) / 10}k`
      : String(value)
  }, [])

  if (chartData.length === 0) {
    return null
  }

  return (
    <Collapsible
      open={!isChartCollapsed}
      onOpenChange={(open) => setIsChartCollapsed(!open)}
      className="border-b"
    >
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
            {isChartCollapsed ? (
              <ChevronRight className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
            Log volume
            <kbd className="ml-1.5 inline-flex h-5 items-center rounded border bg-muted px-1 font-mono text-[12px] text-muted-foreground">
              {isMac ? '⌘' : 'Ctrl'} H
            </kbd>
          </CollapsibleTrigger>
          <div className="flex items-center gap-2 font-mono">
            <SummaryBadge
              label="Trace"
              value={summary.trace}
              color="bg-slate-500"
            />
            <SummaryBadge
              label="Debug"
              value={summary.debug}
              color="bg-violet-500"
            />
            <SummaryBadge
              label="Info"
              value={summary.info}
              color="bg-sky-500"
            />
            <SummaryBadge
              label="Warn"
              value={summary.warn}
              color="bg-amber-500"
            />
            <SummaryBadge
              label="Error"
              value={summary.error}
              color="bg-destructive"
            />
            <SummaryBadge
              label="Fatal"
              value={summary.fatal}
              color="bg-fuchsia-500"
            />
          </div>
        </div>
        <CollapsibleContent forceMount className="data-[state=closed]:hidden">
          <ChartContainer
            config={chartConfig}
            className="h-34 w-full select-none [&_.recharts-wrapper]:!cursor-crosshair mt-2"
          >
            <BarChart accessibilityLayer data={chartData} {...chartProps}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="time"
                scale="time"
                type="number"
                domain={[
                  timeframe?.startDate ? timeframe?.startDate?.getTime() : 0,
                  timeframe?.endDate
                    ? timeframe?.endDate.getTime()
                    : Date.now(),
                ]}
                tickLine={false}
                interval="equidistantPreserveStart"
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) =>
                  value &&
                  format(
                    value,
                    getXAxisDateFormat({
                      startDate: timeframe.startDate,
                      endDate: timeframe.endDate,
                      intervalInSeconds: timeframe.intervalInSeconds,
                    })
                  )
                }
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickCount={4}
                tickFormatter={(val) => `${Math.round(Number(val ?? '0'))}`}
                allowDecimals={false}
              />
              <ChartTooltip
                cursor={{
                  stroke: 'var(--foreground)',
                  strokeDasharray: '4 4',
                  strokeWidth: 1,
                  opacity: 0.5,
                }}
                wrapperStyle={
                  isSelecting ? { visibility: 'hidden' } : undefined
                }
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    hideLabel={false}
                    labelFormatter={tooltipLabelFormatter}
                    valueFormatter={tooltipValueFormatter}
                  />
                }
              />
              <Bar dataKey="trace" stackId="a" fill={chartConfig.trace.color} />
              <Bar dataKey="debug" stackId="a" fill={chartConfig.debug.color} />
              <Bar dataKey="info" stackId="a" fill={chartConfig.info.color} />
              <Bar dataKey="warn" stackId="a" fill={chartConfig.warn.color} />
              <Bar dataKey="error" stackId="a" fill={chartConfig.error.color} />
              <Bar dataKey="fatal" stackId="a" fill={chartConfig.fatal.color} />
              {referenceAreaProps && <ReferenceArea {...referenceAreaProps} />}
            </BarChart>
          </ChartContainer>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

export const LogVolumeChartFallback = () => (
  <div className="px-4 py-3 border-b">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5">
        <Skeleton className="size-4" />
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-5 w-12" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-6 w-14" />
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-6 w-16" />
      </div>
    </div>
    <Skeleton className="h-34 w-full mt-1 mb-0.5" />
  </div>
)
